import React, { useEffect, useState } from "react";
import { Check, Sparkles, Loader2, CreditCard, Settings, Trash2, AlertTriangle, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import MonCompte from "./MonCompte";

const fmtPrice = (n) => (n === 0 || !n ? "0€" : `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: n % 1 ? 2 : 0 })}€`);

function DangerZone() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const del = async () => {
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { confirm_email: confirmEmail });
      toast.success("Votre compte et toutes vos données ont été supprimés.");
      try { await logout(); } catch (e) { /* ignore */ }
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-red-200 p-6" data-testid="danger-zone">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5" /></div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-semibold text-red-600">Supprimer mon compte</h2>
          <p className="text-sm text-brand-green/60 mt-1">Cette action est <b>définitive</b>. Elle supprime votre profil ainsi que toutes vos données : biens, locataires, baux, quittances, finances, états des lieux, messages… Aucune récupération n'est possible.</p>
          <button onClick={() => { setConfirmEmail(""); setOpen(true); }} className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors" data-testid="delete-account-open">
            <Trash2 className="w-4 h-4" /> Supprimer mon compte
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)} data-testid="delete-account-modal">
          <div className="bg-white rounded-3xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-lg font-semibold text-red-600">Confirmer la suppression</h3>
              <button onClick={() => !busy && setOpen(false)} data-testid="delete-account-close"><X className="w-5 h-5 text-brand-green/50" /></button>
            </div>
            <p className="text-sm text-brand-green/60">Pour confirmer, saisissez votre e-mail : <b className="text-brand-greenDeep break-all">{user?.email}</b></p>
            <input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder="votre e-mail" autoComplete="off"
              className="w-full mt-3 px-3 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-red-300 text-sm" data-testid="delete-account-confirm-input" />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setOpen(false)} disabled={busy} className="px-4 py-2.5 rounded-full border border-brand-soft text-brand-greenDeep text-sm font-semibold hover:bg-brand-softer disabled:opacity-60" data-testid="delete-account-cancel">Annuler</button>
              <button onClick={del} disabled={busy || !confirmEmail.trim()} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-semibold disabled:opacity-50" data-testid="delete-account-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Profil() {
  const { user, setUser } = useAuth();
  const [offers, setOffers] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");
  const [edlOffer, setEdlOffer] = useState(null);

  useEffect(() => {
    api.get("/offers").then((r) => setOffers(r.data.offers || [])).catch(() => {});
    api.get("/edl/offer").then((r) => setEdlOffer(r.data)).catch(() => {});
    const pending = localStorage.getItem("pending_upgrade");
    if (pending) {
      localStorage.removeItem("pending_upgrade");
      try {
        const { plan, cycle: c } = JSON.parse(pending);
        if (c) setCycle(c);
        if (plan) setTimeout(() => choose(plan), 300);
      } catch (e) { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentPlan = user?.plan || "Premium";

  const choose = async (name) => {
    setBusy(name);
    try {
      const res = await api.post("/auth/plan-checkout", {
        plan: name,
        cycle,
        origin_url: window.location.origin,
      });
      if (res.data.free) {
        const me = await api.get("/auth/me");
        setUser(me.data);
        toast.success(`Offre « ${name} » activée`);
        setBusy("");
      } else {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setBusy("");
    }
  };

  const manageBilling = async () => {
    setBusy("__portal__");
    try {
      const res = await api.post("/auth/billing-portal", { origin_url: window.location.origin });
      window.location.href = res.data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portail indisponible");
      setBusy("");
    }
  };

  const isReferent = user?.role === "referent";

  return (
    <div className="space-y-6" data-testid="profil-page">
      <MonCompte />
      {isReferent ? (
        <div className="bg-white rounded-3xl border border-brand-soft p-6" data-testid="referent-no-subscription">
          <p className="text-sm text-brand-green/60">En tant que référent partenaire, vous n'avez aucun abonnement à régler : vous percevez vos commissions via vos relevés mensuels. Retrouvez-les dans « Mes relevés ».</p>
        </div>
      ) : (
      <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><CreditCard className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">Mon abonnement</h2>
            <p className="text-sm text-brand-green/55">Offre actuelle : <span className="font-semibold text-brand-greenDeep" data-testid="current-plan">{currentPlan}</span></p>
          </div>
        </div>
        {user?.stripe_customer_id && (
          <button
            onClick={manageBilling}
            disabled={busy === "__portal__"}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-brand-soft bg-white hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold transition-colors disabled:opacity-60"
            data-testid="manage-billing-btn"
          >
            {busy === "__portal__" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
            Gérer / résilier mon abonnement
          </button>
        )}
      </div>

      {user?.edl_addon && (
        <div className="bg-white rounded-3xl border border-brand-soft p-6 flex flex-wrap items-center justify-between gap-4" data-testid="edl-subscription-card">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><Check className="w-5 h-5" /></div>
            <div>
              <h3 className="font-display text-lg font-semibold text-brand-greenDeep">Abonnement « {edlOffer?.name || "État des lieux"} »</h3>
              <p className="text-sm text-brand-green/55">
                Actif{user?.edl_addon_cycle ? ` · ${user.edl_addon_cycle === "yearly" ? "annuel" : "mensuel"}` : ""}
                {edlOffer ? ` · ${user?.edl_addon_cycle === "yearly" ? fmtPrice(edlOffer.price_yearly) + "/an" : fmtPrice(edlOffer.price_monthly) + "/mois"}` : ""}
              </p>
            </div>
          </div>
          {user?.stripe_customer_id && (
            <button onClick={manageBilling} disabled={busy === "__portal__"} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-brand-soft bg-white hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold transition-colors disabled:opacity-60" data-testid="manage-edl-billing-btn">
              {busy === "__portal__" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
              Gérer / résilier mon abonnement États des lieux
            </button>
          )}
        </div>
      )}

      <div className="flex gap-2 p-1 bg-brand-softer rounded-full w-fit">
        <button onClick={() => setCycle("monthly")} className={`px-5 py-2 rounded-full text-sm font-semibold ${cycle === "monthly" ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/55"}`} data-testid="cycle-monthly">Mensuel</button>
        <button onClick={() => setCycle("yearly")} className={`px-5 py-2 rounded-full text-sm font-semibold ${cycle === "yearly" ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/55"}`} data-testid="cycle-yearly">Annuel</button>
      </div>

      <div className="grid md:grid-cols-3 gap-6 items-stretch">
        {offers.map((o) => {
          const isCurrent = o.name === currentPlan;
          const base = cycle === "monthly" ? o.price_monthly : o.price_yearly;
          const promoVal = cycle === "monthly" ? o.promo_price_monthly : o.promo_price_yearly;
          const hasPromo = !!o.promo_active && Number(promoVal) < Number(base || 0);
          const price = hasPromo ? Number(promoVal || 0) : base;
          return (
            <div key={o.id || o.name} className={`relative rounded-3xl p-8 flex flex-col ${o.highlight ? "bg-brand-greenDeep text-white shadow-2xl" : "bg-white border border-brand-soft"}`} data-testid={`offer-${o.name}`}>
              {o.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-lime text-brand-greenDeep text-xs font-bold"><Sparkles className="w-3.5 h-3.5" /> Le plus populaire</span>}
              {hasPromo && <span className="absolute -top-3 right-4 inline-flex items-center px-3 py-1.5 rounded-full bg-red-500 text-white text-xs font-bold" data-testid={`promo-badge-${o.name}`}>{o.promo_label || "Promotion"}</span>}
              <h3 className={`font-display text-2xl font-semibold ${o.highlight ? "text-white" : "text-brand-greenDeep"}`}>{o.name}</h3>
              <p className={`text-sm mt-1 ${o.highlight ? "text-white/70" : "text-brand-green/60"}`}>{o.desc}</p>
              <div className="mt-6 flex items-end gap-2">
                {hasPromo && <span className={`mb-2 text-2xl font-semibold line-through ${o.highlight ? "text-white/40" : "text-brand-green/35"}`} data-testid={`old-price-${o.name}`}>{fmtPrice(base)}</span>}
                <span className={`font-display text-5xl font-bold ${o.highlight ? "text-white" : "text-brand-greenDeep"}`} data-testid={`price-${o.name}`}>{price === 0 ? "Gratuit" : fmtPrice(price)}</span>
                <span className={`mb-2 text-sm ${o.highlight ? "text-white/60" : "text-brand-green/50"}`}>{price === 0 ? (hasPromo ? "en promo" : "/ pour toujours") : cycle === "monthly" ? "/ mois" : "/ an"}</span>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {(o.services || []).map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${o.highlight ? "bg-brand-lime" : "bg-brand-soft"}`}><Check className="w-3 h-3 text-brand-greenDeep" strokeWidth={3} /></span>
                    <span className={`text-sm ${o.highlight ? "text-white/85" : "text-brand-green/75"}`}>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => choose(o.name)}
                disabled={isCurrent || busy === o.name}
                className={`mt-8 w-full py-3.5 rounded-full font-semibold transition-colors disabled:opacity-60 ${o.highlight ? "bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep" : "bg-brand-soft hover:bg-brand-mint text-brand-greenDeep"}`}
                data-testid={`choose-${o.name}`}
              >
                {busy === o.name ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : isCurrent ? "Offre actuelle" : "Choisir cette offre"}
              </button>
            </div>
          );
        })}
      </div>
      </>
      )}
      <DangerZone />
    </div>
  );
}
